/**
 * The hub's small shared words and helpers: goal labels and scenes, the
 * query-string and placeholder helpers, the search said in words.
 *
 * Plain data in a module of its own: the public homepage's personal band
 * (VisitorForYou) uses them, and importing them from HubHero/GoalPicker
 * pulled the whole signed-in hub (hero, spotlight, goal picker, search
 * bar, location picker) into every visitor's first load (2026-10-19).
 */
import { formatPrice } from "@/lib/api";
import type { TranslationKey } from "@/lib/i18n";
import { isSeparate, kindChipKey, kindsOf } from "@/lib/property-kinds";
import type { Intent } from "@/lib/use-personal";

export const GOAL_LABEL: Record<Intent, TranslationKey> = {
  buy: "home.ws.goal.buy",
  rent: "home.ws.goal.rent",
  sell: "home.ws.goal.sell",
  invest: "home.ws.goal.invest",
  rent_out: "home.ws.goal.rent_out",
  agent: "home.ws.goal.agent",
  commercial: "home.ws.goal.commercial",
  garage: "home.ws.goal.garage",
};

/** Buy, invest and sell reuse the finder's scenes; the two rental sides,
 *  the office and the garage have their own (ops/scripts/
 *  gen_finder_illustrations.py --set goals). */
export const GOAL_ART: Record<Intent, string> = {
  buy: "/illustrations/finder/entry",
  rent: "/illustrations/goals/rent",
  invest: "/illustrations/finder/opts/intent_invest",
  sell: "/illustrations/finder/deed",
  rent_out: "/illustrations/goals/rent_out",
  agent: "/illustrations/finder/floors",
  commercial: "/illustrations/goals/commercial",
  garage: "/illustrations/goals/garage",
};

export function qs(params: Record<string, string | string[] | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === "") continue;
    for (const x of Array.isArray(v) ? v : [v]) u.append(k, x);
  }
  return u.toString();
}

export const fill = (s: string, vars: Record<string, string | number>) =>
  Object.entries(vars).reduce((a, [k, v]) => a.split(`{${k}}`).join(String(v)), s);

/** The person's search, said in words: "Yasamal · 2-3 otaq · 300 000 AZN-dək". */
export function criteria(
  t: (k: TranslationKey) => string,
  params: Record<string, string | string[]> | null,
  names: string[],
): string {
  if (!params) return "";
  const parts: string[] = [];
  // An office's or a garage's search leads with what it is ("Ofis").
  const cat = Array.isArray(params.category) ? params.category[0] : params.category;
  if (isSeparate(cat)) {
    const kinds = ([] as string[]).concat(params.property_kind ?? []).filter((k) => (kindsOf(cat) as readonly string[]).includes(k));
    parts.push(kinds.length ? kinds.map((k) => t(kindChipKey(k))).join(", ") : t(`listings.filter.category.${cat}` as TranslationKey));
  }
  if (names.length) parts.push(names.slice(0, 2).join(", "));
  const rooms = ([] as string[]).concat(params.rooms ?? []).map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (rooms.length) {
    // Five and up is one label (the finder sends 5..10).
    const labels = [...new Set(rooms.map((n) => (n >= 5 ? "5+" : String(n))))];
    const r = labels.length > 1 && rooms[rooms.length - 1] - rooms[0] === rooms.length - 1
      ? `${labels[0]}-${labels[labels.length - 1]}`
      : labels.join(", ");
    parts.push(fill(t("home.hub.crit.rooms"), { r }));
  }
  const min = params.price_min ? Number(params.price_min) : null;
  const max = params.price_max ? Number(params.price_max) : null;
  if (min && max) parts.push(`${formatPrice(min)}-${formatPrice(max)} AZN`);
  else if (max) parts.push(fill(t("home.hub.crit.upto"), { p: formatPrice(max) }));
  else if (min) parts.push(fill(t("home.hub.crit.from"), { p: formatPrice(min) }));
  return parts.join(" · ");
}
